import { type FC, lazy, Suspense, useCallback, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { isLocale, LOCALE_US } from 'dibs-intl/exports/locales';
import { getLanguageOptions } from 'dibs-intl/exports/languages';
import { useClientState } from 'dibs-react-hooks/exports/useClientState';
import { useCurrency } from '../CurrencyProvider/useCurrency';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import getCurrencySymbol from 'dibs-currency-helpers/exports/getCurrencySymbol';

import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers';

import { Button } from 'dibs-elements/exports/Button';
import World from 'dibs-icons/exports/legacy/World';

import { trackNavigationClick } from '../../helpers/trackingHelpers';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './DisplaySettingsCta.scss';

import { type DisplaySettingsCta_viewer$key } from './__generated__/DisplaySettingsCta_viewer.graphql';
import { type DisplaySettingsCta_user$key } from './__generated__/DisplaySettingsCta_user.graphql';

const DisplaySettingsModalLazy = lazy(
    () => import(/* webpackChunkName: "DisplaySettingsModal" */ './DisplaySettingsModal')
);

const messages = defineMessages({
    label: {
        id: 'dbl.DisplaySettings.CtaLabel',
        defaultMessage: 'Display Settings',
    },
});

const viewerFragment = graphql`
    fragment DisplaySettingsCta_viewer on Viewer {
        currencies
        geography {
            countries {
                name
                abbreviation
            }
        }
    }
`;

const userFragment = graphql`
    fragment DisplaySettingsCta_user on User {
        ...DisplaySettingsModal_user
        addressBook {
            personalAddress {
                country
            }
        }
    }
`;

type Props = {
    viewer: DisplaySettingsCta_viewer$key;
    user: DisplaySettingsCta_user$key | null | undefined;
    placement?: 'footer' | 'mobileFooter';
};

export const DisplaySettingsCta: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    placement = 'footer',
}) => {
    const isClient = useClientState();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const intl = useIntl();
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const isInMobileFooter = placement === 'mobileFooter';

    const { currency: currentCurrency, showCurrency } = useCurrency();

    const onModalClose = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    if (!isClient) {
        return null;
    }

    if (!showCurrency) {
        return <div className={classnames(styles.placeholder, dibsCss.wFull)} />;
    }

    const currentCountry =
        user?.addressBook?.personalAddress?.country || getUserSessionCountryCode();

    const languageOptions = getLanguageOptions();

    const currentLocaleLabel = isLocale(intl.locale)
        ? languageOptions.find(c => c.value === intl.locale)?.label
        : languageOptions.find(c => c.value === LOCALE_US)?.label;

    const countries = (viewer.geography?.countries || [])
        .map(country => {
            if (country?.name && country.abbreviation) {
                return {
                    value: country.abbreviation,
                    label: country.name,
                };
            }
            return null;
        })
        .filter(filterNulls);

    const currencies = (
        (viewer?.currencies || []).map(currency => {
            const currencySymbol = getCurrencySymbol(currency);
            return currency
                ? {
                      label: currencySymbol ? `${currencySymbol} ${currency}` : currency,
                      value: currency,
                  }
                : null;
        }) || []
    ).filter(filterNulls);

    const currentCurrencyLabel = currencies.find(c => c.value === currentCurrency)?.label;
    const countryDisplayLabel = countries.find(c => c.value === currentCountry)?.label;

    return (
        <div data-tn={`display-settings-${placement}`}>
            <div
                className={classnames(
                    styles.modalButtonContainer,
                    dibsCss.flex,
                    dibsCss.justifyEnd
                )}
            >
                <Button
                    className={styles.modalButton}
                    onClick={event => {
                        setIsModalOpen(true);
                        trackNavigationClick(
                            { label: 'region selector modal entry - intl' },
                            event
                        );
                    }}
                    type="textCta"
                    size="small"
                    dataTn="display-settings-modal-cta"
                    ariaLabel={intl.formatMessage(messages.label)}
                >
                    <div className={classnames(dibsCss.flex, dibsCss.itemsCenter)}>
                        <World
                            className={classnames(dibsCss.wSmall, dibsCss.hSmall, dibsCss.mrXsmall)}
                        />
                        {countryDisplayLabel && !isInMobileFooter && (
                            <span className={styles.labelSeparator}>{countryDisplayLabel}</span>
                        )}
                        <span className={styles.labelSeparator}>{currentLocaleLabel}</span>
                        <span>{currentCurrencyLabel}</span>
                    </div>
                </Button>
            </div>
            {isModalOpen && (
                <Suspense fallback={null}>
                    <DisplaySettingsModalLazy
                        user={user}
                        currencies={currencies}
                        countries={countries}
                        languageOptions={languageOptions}
                        currentCountry={currentCountry}
                        currentCurrency={currentCurrency}
                        onClose={onModalClose}
                    />
                </Suspense>
            )}
        </div>
    );
};
